import React, { useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  hasDuplicateFinanceCode,
  INVOICE_FINANCE_BUCKETS,
  normalizeFinanceCodeAliases,
  normalizeInvoiceBuckets,
} from "../../../../services/financeCodes";

const trimString = (value) => String(value ?? "").trim();
const nowIso = () => new Date().toISOString();
const generateId = () => (typeof crypto?.randomUUID === "function"
  ? crypto.randomUUID()
  : `finance-code-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const emptyFinanceCode = (sortOrder = 10) => {
  const timestamp = nowIso();
  return {
    id: generateId(),
    code: "",
    label: "",
    description: "",
    enabled: true,
    appliesToBills: true,
    appliesToInvoices: false,
    sortOrder,
    aliases: [],
    invoiceBuckets: [],
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

export default function FinanceCodesSettingsPanel({
  financeCodes = [],
  setFinanceCodes,
  onInstallStarterPack,
  feedbackMessage = "",
}) {
  const sortedCodes = useMemo(() => (
    [...financeCodes].sort((left, right) => Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0))
  ), [financeCodes]);
  const assignedInvoiceBuckets = useMemo(() => {
    const next = new Map();
    sortedCodes
      .filter((codeRow) => codeRow?.appliesToInvoices === true)
      .forEach((codeRow) => {
        normalizeInvoiceBuckets(codeRow?.invoiceBuckets).forEach((bucketKey) => {
          if (!next.has(bucketKey)) {
            next.set(bucketKey, []);
          }
          next.get(bucketKey).push(codeRow);
        });
      });
    return next;
  }, [sortedCodes]);

  const updateRows = (nextRows) => {
    setFinanceCodes(nextRows.map((row, index) => ({
      ...row,
      code: trimString(row?.code || ""),
      label: trimString(row?.label || "") || trimString(row?.code || ""),
      description: String(row?.description || ""),
      sortOrder: Number.isFinite(Number(row?.sortOrder)) ? Number(row.sortOrder) : (index + 1) * 10,
      aliases: normalizeFinanceCodeAliases(row?.aliases),
      invoiceBuckets: normalizeInvoiceBuckets(row?.invoiceBuckets),
      updatedAt: nowIso(),
    })));
  };

  const updateRow = (index, field, value) => {
    updateRows(sortedCodes.map((row, rowIndex) => (
      rowIndex === index
        ? {
            ...row,
            [field]: field === "enabled" || field === "appliesToBills" || field === "appliesToInvoices"
              ? value
              : value,
          }
        : row
    )));
  };

  const moveRow = (index, direction) => {
    const next = [...sortedCodes];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= next.length) return;
    const temp = next[index];
    next[index] = next[targetIndex];
    next[targetIndex] = temp;
    updateRows(next.map((row, rowIndex) => ({ ...row, sortOrder: (rowIndex + 1) * 10 })));
  };

  const addFinanceCode = () => {
    updateRows([...sortedCodes, emptyFinanceCode((sortedCodes.length + 1) * 10)]);
  };

  const removeFinanceCode = (index) => {
    const row = sortedCodes[index];
    const confirmed = window.confirm(
      `Remove finance code "${row?.label || row?.code || "Untitled"}"? If this code has already been used historically, it should generally be disabled instead of removed.`
    );
    if (!confirmed) return;
    updateRows(sortedCodes.filter((_, rowIndex) => rowIndex !== index));
  };

  if (!sortedCodes.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3, display: "grid", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Finance Codes
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            No finance codes have been created for this instance.
          </Typography>
        </Box>
        {feedbackMessage ? (
          <Typography variant="body2" sx={{ color: "success.main" }}>
            {feedbackMessage}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={addFinanceCode}>
            Add Finance Code
          </Button>
          <Button variant="outlined" onClick={onInstallStarterPack}>
            Install Billing Starter Pack
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Finance Codes
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage reusable finance codes for bills now, with invoice scope already represented in the data model.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={addFinanceCode}>
            Add Finance Code
          </Button>
          <Button variant="outlined" onClick={onInstallStarterPack}>
            Install Billing Starter Pack
          </Button>
        </Box>
      </Box>

      {feedbackMessage ? (
        <Typography variant="body2" sx={{ color: "success.main" }}>
          {feedbackMessage}
        </Typography>
      ) : null}

      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        Disable codes instead of deleting them when they may already exist on historical bills.
      </Typography>

      {sortedCodes.map((row, index) => {
        const duplicateCode = hasDuplicateFinanceCode(sortedCodes, row?.code, row?.id);
        const duplicateInvoiceBuckets = normalizeInvoiceBuckets(row?.invoiceBuckets).filter((bucketKey) => {
          const owners = assignedInvoiceBuckets.get(bucketKey) || [];
          return owners.some((owner) => String(owner?.id) !== String(row?.id));
        });
        return (
          <Paper key={row.id || `${row.code}-${index}`} variant="outlined" sx={{ p: 2, display: "grid", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {trimString(row?.label || row?.code) || `Finance Code ${index + 1}`}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton size="small" onClick={() => moveRow(index, -1)} disabled={index === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveRow(index, 1)} disabled={index === sortedCodes.length - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeFinanceCode(index)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
              <TextField
                size="small"
                label="Code"
                value={row.code || ""}
                onChange={(e) => updateRow(index, "code", e.target.value)}
                error={duplicateCode}
                helperText={duplicateCode ? "Finance code values must be unique within the instance, even when another matching code is disabled." : "Stored code value. Spaces are allowed."}
              />
              <TextField
                size="small"
                label="Label"
                value={row.label || ""}
                onChange={(e) => updateRow(index, "label", e.target.value)}
                helperText="Display label shown to users."
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 180px" }, gap: 1.5 }}>
              <TextField
                size="small"
                label="Description"
                value={row.description || ""}
                onChange={(e) => updateRow(index, "description", e.target.value)}
              />
              <TextField
                size="small"
                label="Sort Order"
                type="number"
                value={row.sortOrder ?? ""}
                onChange={(e) => updateRow(index, "sortOrder", e.target.value)}
              />
            </Box>

            <TextField
              size="small"
              label="Aliases"
              value={Array.isArray(row.aliases) ? row.aliases.join(", ") : ""}
              onChange={(e) => updateRow(index, "aliases", e.target.value)}
              helperText="Comma or newline separated aliases used for bill auto-mapping."
              multiline
              minRows={2}
            />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={row.enabled !== false}
                    onChange={(e) => updateRow(index, "enabled", e.target.checked)}
                  />
                )}
                label="Enabled"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={row.appliesToBills !== false}
                    onChange={(e) => updateRow(index, "appliesToBills", e.target.checked)}
                  />
                )}
                label="Applies to Bills"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={row.appliesToInvoices === true}
                    onChange={(e) => updateRow(index, "appliesToInvoices", e.target.checked)}
                  />
                )}
                label="Applies to Invoices"
              />
            </Box>

            {row.appliesToInvoices === true ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Invoice Buckets
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Map this finance code to one or more invoice source buckets. Tax and rounding are intentionally excluded.
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 0.5 }}>
                  {INVOICE_FINANCE_BUCKETS.map((bucket) => {
                    const selectedBuckets = normalizeInvoiceBuckets(row?.invoiceBuckets);
                    const isChecked = selectedBuckets.includes(bucket.key);
                    const bucketOwners = (assignedInvoiceBuckets.get(bucket.key) || []).filter(
                      (owner) => String(owner?.id) !== String(row?.id)
                    );
                    const claimedByOther = bucketOwners.length > 0;
                    const disabled = claimedByOther && !isChecked;
                    const ownerLabel = bucketOwners[0]?.label || bucketOwners[0]?.code || "another finance code";

                    return (
                      <Box
                        key={bucket.key}
                        sx={{
                          border: "1px solid",
                          borderColor: disabled ? "warning.light" : "divider",
                          borderRadius: 1,
                          p: 1,
                          opacity: disabled ? 0.7 : 1,
                        }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={isChecked}
                              disabled={disabled}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...selectedBuckets, bucket.key]
                                  : selectedBuckets.filter((key) => key !== bucket.key);
                                updateRow(index, "invoiceBuckets", normalizeInvoiceBuckets(next));
                              }}
                            />
                          )}
                          label={bucket.label}
                        />
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
                          {bucket.description}
                        </Typography>
                        {disabled ? (
                          <Typography variant="caption" sx={{ display: "block", color: "warning.main", mt: 0.5 }}>
                            Already assigned to {ownerLabel}.
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
                {duplicateInvoiceBuckets.length ? (
                  <Typography variant="caption" sx={{ color: "warning.main" }}>
                    Some selected invoice buckets are also assigned to another invoice finance code. Later invoice export wiring will use the first matching active finance code by sort order.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Paper>
        );
      })}
    </Box>
  );
}
